/**
 * optionsTraderV2Service -- client for the TT Options Trader v2 (paper) API.
 *
 * WAF rule (RateLimit100Per5Min): every v2 page loads with at most 2
 * requests; there is no per-engine fan-out. Use the aggregate routes
 * (/v2/trades, /v2/analytics, /v2/audit) instead of per-engine ones.
 *
 * All calls send Authorization: Bearer <jwt> and X-Client: web. API errors
 * come back as { error: { code, message, detail } } and are thrown as
 * V2ApiError carrying that message.
 */
import type { AccountAnalytics } from './analyticsService';

const API_BASE = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export class V2ApiError extends Error {
  status: number;
  code?: string;
  detail?: any;
  constructor(message: string, status: number, code?: string, detail?: any) {
    super(message);
    this.name = 'V2ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

async function req<T>(token: string, method: string, path: string, body?: any, extra?: Record<string, string>): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, 'X-Client': 'web', ...(extra || {}) };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API_BASE}${path}`, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  const data: any = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = data && data.error;
    const msg = (typeof e === 'string' && e) || (e && e.message) || (typeof data?.message === 'string' && data.message) || `API error: ${res.status}`;
    throw new V2ApiError(msg, res.status, e && typeof e === 'object' ? e.code : undefined, e && typeof e === 'object' ? e.detail : undefined);
  }
  return data as T;
}

/** For the aggregate read routes: a 404 (route not deployed yet / nothing there) is an empty result, not an error. */
async function getOrEmpty<T>(token: string, path: string, empty: T): Promise<T> {
  try {
    return await req<T>(token, 'GET', path);
  } catch (e) {
    if (e instanceof V2ApiError && e.status === 404) return empty;
    throw e;
  }
}

// ---------- Engines ----------

export type V2Strategy = 'naked_put' | 'bear_call_spread' | 'jade_lizard' | 'iron_condor' | 'manual';
export type V2EngineState = 'inactive' | 'active' | 'paused' | 'retiring' | 'archived' | string;

export interface V2Engine {
  engineId: string;
  name?: string;
  state: V2EngineState;
  mode: 'paper' | 'live' | string;
  management?: string;
  accountNumber: string;
  symbol: string;
  assetClass?: string;
  strategy: V2Strategy;
  templateId?: string;
  templateVersion?: number;
  overrides?: Record<string, any>;
  effectiveConfig: Record<string, any>;
  configVersion?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  engineType?: 'notches' | 'manual';
  manualCounts?: V2ManualCounts;
  suspendedReason?: 'v1_active' | 'v1_read_failed';
}

export interface V2SchemaField {
  type: string;
  enum?: string[];
  min?: number;
  max?: number;
  default?: any;
  nullable?: boolean;
  locked?: boolean;
  identity?: boolean;
  risk?: 'up' | 'down' | 'neutral';
  properties?: Record<string, V2SchemaField>;
}
export interface V2Schema { version: number; fields: Record<string, V2SchemaField> }
export interface V2Template { templateId: string; version: number; strategy: V2Strategy; description?: string; config: Record<string, any> }

export interface V2Requires { persona?: string; broker?: string | number; naked?: boolean; acks?: string[] }
export interface V2CreatePreview {
  ok: true;
  planId: string;
  effectiveConfig: Record<string, any>;
  diffFromTemplate?: Record<string, { template: any; value: any; risk?: string }>;
  requires?: V2Requires;
}
export interface V2PatchPreview {
  ok: true;
  planId: string;
  effectiveConfig: Record<string, any>;
  diffFromCurrent?: Record<string, { from: any; to: any }>;
  /** Present when the patch renames the engine (name is a display label, not config). */
  nameChange?: { from: string | null; to: string | null };
}
export interface V2CreateInput {
  accountNumber: string;
  symbol?: string;
  strategy: V2Strategy;
  templateId?: string;
  name?: string;
  overrides: Record<string, any>;
}

const enc = encodeURIComponent;

export async function listEngines(token: string): Promise<V2Engine[]> {
  return (await req<{ engines: V2Engine[] }>(token, 'GET', '/v2/engines')).engines || [];
}
export const getSchema = (token: string) => req<V2Schema>(token, 'GET', '/v2/schema');
export async function getTemplates(token: string): Promise<V2Template[]> {
  return (await req<{ templates: V2Template[] }>(token, 'GET', '/v2/templates')).templates || [];
}
export const previewCreate = (token: string, input: V2CreateInput) => req<V2CreatePreview>(token, 'POST', '/v2/engines/preview', input);
export const applyCreate = (token: string, planId: string, idempotencyKey: string) =>
  req<{ engine: V2Engine }>(token, 'POST', '/v2/engines', { planId }, { 'Idempotency-Key': idempotencyKey });
/** `name`: omit to leave unchanged; '' clears it (card falls back to the synthesized label). */
export const previewPatch = (token: string, engineId: string, overrides: Record<string, any>, name?: string) =>
  req<V2PatchPreview>(token, 'POST', `/v2/engines/${enc(engineId)}/preview-patch`, name === undefined ? { overrides } : { overrides, name });
export const applyPatch = (token: string, engineId: string, planId: string) =>
  req<{ engine: V2Engine }>(token, 'PATCH', `/v2/engines/${enc(engineId)}`, { planId });
export const activateEngine = (token: string, engineId: string) => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/activate`, {});
export const pauseEngine = (token: string, engineId: string) => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/pause`, {});
export const resumeEngine = (token: string, engineId: string) => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/resume`, {});
export const deleteEngine = (token: string, engineId: string) => req<{ engine: V2Engine }>(token, 'DELETE', `/v2/engines/${enc(engineId)}`);

// ---------- Trades ----------

export interface V2Leg {
  role?: string;
  side?: 'short' | 'long' | string;
  type?: 'P' | 'C' | string;
  strike?: number;
  contractSymbol?: string;
  delta?: number | null;
  expiration?: string;
  ratio?: number;
}

export interface V2Trade {
  tradeId: string;
  engineId?: string;
  engineName?: string;
  symbol?: string;
  strategy?: string;
  shape?: string;
  legs?: V2Leg[];
  quantity?: number;
  status: string;
  mode?: string;
  entryCredit?: number | null;
  exitDebit?: number | null;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
  spreadWidth?: number | null;
  maxProfit?: number | null;
  maxLoss?: number | null;
  expiration?: string;
  dte?: number | null;
  spotAtEntry?: number | null;
  exitReason?: string | null;
  holdDays?: number | null;
  accountLast4?: string | null;
  createdAt: string;
  closedAt?: string | null;
  source?: 'manual_pickup';
  management?: V2Management;
  exitRules?: V2ExitRules;
  alertsMuted?: boolean;
  entryUnknown?: boolean;
  openQuantity?: number;
  structureBroken?: boolean;
  ratioLegs?: boolean;
  pickupState?: 'moving' | 'done';
  coveredShares?: number;   // D10: strategy 'covered_call'
  coverageLost?: boolean;   // D10
  needsAttention?: boolean;
  attentionReason?: string | null;
  closeReason?: string | null;
  pnlSource?: string | null;
  pnlIncomplete?: boolean;
  lastExitEval?: { action?: string; reason?: string | null; wouldHave?: string | null; suppressed?: string | null } | null;
}

export interface V2TradesResponse {
  trades: V2Trade[];
  total: number;
  counts: { all: number; open: number; closed: number };
}

export function getTrades(token: string, status: 'all' | 'open' | 'closed' = 'all', limit = 200): Promise<V2TradesResponse> {
  return getOrEmpty<V2TradesResponse>(token, `/v2/trades?status=${status}&limit=${limit}`, { trades: [], total: 0, counts: { all: 0, open: 0, closed: 0 } });
}

// ---------- Analytics ----------

/** Same shape as v1 AccountAnalytics; balance may be null and header.unrealized may be null. */
export type V2AccountAnalytics = Omit<AccountAnalytics, 'balance' | 'header' | 'userId' | 'sinceIso' | 'txnBreakdown'> & {
  userId?: string;
  sinceIso?: string;
  txnBreakdown?: AccountAnalytics['txnBreakdown'] | null;
  balance?: AccountAnalytics['balance'] | null;
  header: Omit<AccountAnalytics['header'], 'unrealized'> & { unrealized: number | null };
};
export interface V2AnalyticsResponse { accounts: V2AccountAnalytics[]; accountCount: number }

export function getAnalytics(token: string): Promise<V2AnalyticsResponse> {
  return getOrEmpty<V2AnalyticsResponse>(token, '/v2/analytics', { accounts: [], accountCount: 0 });
}

// ---------- Audit ----------

export interface V2AuditEvent {
  eventId: string;
  eventType: string;
  status: 'success' | 'failure' | 'skip' | 'warning' | string;
  summary?: string;
  detail?: Record<string, any> | null;
  timestamp: string;
  durationMs?: number | null;
}

export interface V2AuditRun {
  runId: string;
  engineId: string;
  engineName?: string;
  symbol?: string;
  strategy?: string;
  startedAt: string;
  updatedAt?: string;
  status?: string;
  skipReason?: string | null;
  origin?: string;
  attempts?: number;
  trades: number;
  skips: number;
  errors: number;
  events: V2AuditEvent[];
}

export function getAudit(token: string, limit = 300): Promise<{ runs: V2AuditRun[] }> {
  return getOrEmpty<{ runs: V2AuditRun[] }>(token, `/v2/audit?limit=${limit}`, { runs: [] });
}

// ---------- SP4: manual engine, alerts, trade patches ----------
export type V2Management = 'alerts' | 'managed';
export interface V2ManualCounts { positions: number; managed: number; alertsOnly: number; entryUnknown: number }
export interface V2AlertSettings { enabled: boolean; email: boolean; sms: boolean }
export interface V2ExitRules {
  profitTargetPct?: number; stopLossMultiplier?: number; stopLossMinDte?: number; preExpiryBufferPts?: number;
  lossCloseMode?: 'auto' | 'alerts'; profitCloseMode?: 'smart' | 'strict'; hardStopMultiplier?: number;
}
export type V2TradeChange = { management: V2Management } | { exitRules: Partial<Record<keyof V2ExitRules, number | string | null>> } | { alertsMuted: boolean } | { entryPrice: number } | { split: true };
export interface V2TradePatchPreview { planId: string; tradeId: string; change: V2TradeChange; before: Record<string, any>; after: Record<string, any>; warnings: string[] }
export const previewTradePatch = (token: string, tradeId: string, change: V2TradeChange) =>
  req<V2TradePatchPreview>(token, 'POST', `/v2/trades/${enc(tradeId)}/preview-patch`, change);
export const applyTradePatch = (token: string, tradeId: string, planId: string) =>
  req<{ trade: V2Trade; trades?: V2Trade[] }>(token, 'PATCH', `/v2/trades/${enc(tradeId)}`, { planId });
export const setEngineMode = (token: string, engineId: string, mode: 'paper' | 'live') => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/mode`, { mode });
export const setEngineManagement = (token: string, engineId: string, management: 'auto' | 'hands_off') => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/management`, { management });
export const retireEngine = (token: string, engineId: string) => req<{ engine: V2Engine }>(token, 'POST', `/v2/engines/${enc(engineId)}/retire`, {});
export const previewAlertSettings = (token: string, engineId: string, alerts: V2AlertSettings) => previewPatch(token, engineId, { alerts });
export const previewManualEngine = (token: string, accountNumber: string, name?: string) =>
  req<V2CreatePreview>(token, 'POST', '/v2/engines/preview', { templateId: 'manual', accountNumber, ...(name ? { name } : {}) });
